import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatDistanceToNow } from 'date-fns';
import { DischargePatient } from '~/types/discharge';
import { useDischargeContext } from '~/hooks/discharge/useDischargeContext';

interface Props {
  patient: DischargePatient;
}

// Simulated latency for the automated agent to "call" the agency again.
const REQUEST_DELAY_MS = 1500;

const FollowUpSection: React.FC<Props> = ({ patient }) => {
  const { requestAgencyUpdate } = useDischargeContext();
  const [requesting, setRequesting] = useState(false);
  const update = patient.agencyUpdate;

  if (!update) {
    return (
      <Typography variant="body2" color="text.secondary">
        No agency update collected yet.
      </Typography>
    );
  }

  const handleRequest = () => {
    setRequesting(true);
    window.setTimeout(() => {
      requestAgencyUpdate(patient.id);
      setRequesting(false);
    }, REQUEST_DELAY_MS);
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: '#fafafa',
        p: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <SmartToyOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>
          COLLECTED VIA AUTOMATED AGENT CALL
        </Typography>
        <Chip
          size="small"
          icon={update.seen ? <CheckCircleIcon /> : <ScheduleIcon />}
          label={update.seen ? 'Patient seen' : 'Not yet seen'}
          color={update.seen ? 'success' : 'warning'}
          variant="outlined"
          sx={{ ml: 'auto' }}
        />
      </Stack>

      <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.5 }}>
        {update.summary}
      </Typography>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="caption" color="text.secondary">
          {requesting
            ? 'Contacting agency…'
            : `Updated ${formatDistanceToNow(new Date(update.updatedAt), { addSuffix: true })}`}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={requesting ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={handleRequest}
          disabled={requesting}
        >
          Request update
        </Button>
      </Stack>
    </Box>
  );
};

export default FollowUpSection;
